import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dispatchMessage, { formatDateString, formatter } from 'app/main/apps/common/AuraFunctions';
import showConfirmationDialog from 'app/main/apps/common/AuraSimpleConfirmDialogs';
import FuseUtils from '@fuse/utils/FuseUtils';
import authPermissions from 'app/auth/authPermissions';
import { recordInvoiceDiscrepancyDecision, syncInvoiceFromOrder } from '../../store/orderSlice';
import {
  differenceLabel,
  differenceSentence,
  discrepancyForDisplay,
  discrepancyGuidance,
  netDifference,
  noteTypeFor,
  recommendedAction,
  syncResultMessage,
} from './invoiceDiscrepancyUtils';
import InvoiceDiscrepancyHelpDialog from './InvoiceDiscrepancyHelpDialog';
import RaiseAdjustmentNoteDialog from './RaiseAdjustmentNoteDialog';

const DECISION_LABELS = {
  ACCEPTED: 'Kept as it is',
  FLAGGED_FOR_CORRECTION: 'Flagged for correction',
  REOPENED: 'Decision undone',
};

const COMPONENT_LABELS = {
  product: 'Product',
  vas: 'VAS',
  surcharge: 'Surcharge',
  shipping: 'Shipping',
};

function formatAmount(row) {
  if (row.order_amount_available === false) {
    return 'Not stored separately on the order';
  }
  if (row.order_amount != null) {
    return formatter.format(row.order_amount);
  }
  return '—';
}

function compactSummaryLine(displayRows) {
  if (!displayRows.length) {
    return null;
  }
  if (displayRows.length === 1) {
    const row = displayRows[0];
    return `Order ${row.order_no || row.order_id} · ${COMPONENT_LABELS[row.component] || row.component} · ${differenceLabel(row.delta)}`;
  }
  return `${displayRows.length} differences · ${differenceLabel(netDifference(displayRows))} overall`;
}

function syncConfirmMessage(displayRows) {
  const summary = compactSummaryLine(displayRows);
  if (summary) {
    return `This will rewrite the invoice lines to match the current order (${summary}). This cannot be undone. Continue?`;
  }
  return 'This will rewrite the invoice lines to match the current order. This cannot be undone. Continue?';
}

/**
 * PGSS-5304 — surfaces per-order invoice vs current order figures.
 * Neutral labels; never claims either side is authoritative.
 * Visible to Staff/Admin only (not Sales, Customer, or Supplier).
 * readOnly: view surfaces (InvoiceTab, OrderDialog eye, ViewOrderDialog) — info only;
 * Sync/Accept/Flag/Raise: ConfirmInvoice, UpdateInvoice (DS/DP), and ReviewInvoiceDiscrepancy
 * (Staff Invoices after Invoiced — PGSS-5306).
 */
function InvoiceDiscrepancyPanel({ invoiceId, discrepancy, onUpdated, onSynced, onNoteRaised, compact, readOnly = false }) {
  const dispatch = useDispatch();
  const currentUser = useSelector(({ user }) => user);
  const canDecide = currentUser && FuseUtils.hasPermission(authPermissions.invoiceDiscrepancy, currentUser.role);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Compact dialogs: start collapsed so Confirm/Update stay short, unless one summary line can't say what differs.
  const [detailsOpen, setDetailsOpen] = useState(
    !compact || (discrepancy?.components || []).filter((row) => row.mismatched).length > 1
  );
  const [raiseOpen, setRaiseOpen] = useState(false);
  // Once a note is raised from this panel, the next Raise starts with an empty amount.
  const [noteRaised, setNoteRaised] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Staff/Admin only — other roles never get this payload from the API.
  if (!canDecide || !discrepancy || !invoiceId) {
    return null;
  }

  const {
    has_discrepancy: hasDiscrepancy,
    warning_active: warningActive,
    components = [],
    latest_decision: latestDecision,
    sync_available: syncAvailable,
  } = discrepancy;

  if (!hasDiscrepancy && !latestDecision) {
    return null;
  }

  const displayRows = (components || []).filter((row) => row.mismatched);
  // PGSS-5862: net, with its sign: +10 and -10 owe nothing. Summing the sizes suggested 20.
  const netDelta = netDifference(displayRows);
  const defaultNoteAmount = Math.abs(netDelta);
  const defaultNoteType = noteTypeFor(netDelta);
  const action = recommendedAction(discrepancy, netDelta, noteRaised);

  const handleDecision = (decision) => {
    setSubmitting(true);
    dispatch(recordInvoiceDiscrepancyDecision({
      id: invoiceId,
      decision,
      note: note || undefined,
    }))
      .unwrap()
      .then((payload) => {
        if (onUpdated && payload?.discrepancy) {
          onUpdated(payload.discrepancy);
        }
        setNote('');
      })
      .catch((err) => {
        dispatchMessage(
          dispatch,
          typeof err === 'string' && err ? err : 'Unable to save the discrepancy decision',
          'error'
        );
      })
      .finally(() => setSubmitting(false));
  };

  const runSyncFromOrder = () => {
    setSubmitting(true);
    dispatch(syncInvoiceFromOrder({ id: invoiceId }))
      .unwrap()
      .then((payload) => {
        // PGSS-5309: when Sync clears the mismatch, pass null so parents unmount the banner.
        if (onUpdated && payload && Object.prototype.hasOwnProperty.call(payload, 'discrepancy')) {
          onUpdated(discrepancyForDisplay(payload.discrepancy));
        }
        if (onSynced) {
          onSynced(payload);
        }
        // PGSS-5612: only claim a match when the re-run comparison agrees.
        const { message, variant } = syncResultMessage(payload?.discrepancy);
        dispatchMessage(dispatch, message, variant, variant === 'success' ? 3000 : 12000);
      })
      .catch((err) => {
        const message = typeof err === 'string' && err
          ? err
          : 'Unable to sync the invoice from the order';
        dispatchMessage(dispatch, message, 'error');
      })
      .finally(() => setSubmitting(false));
  };

  const handleSyncFromOrder = () => {
    showConfirmationDialog(dispatch, {
      title: 'Update invoice to match order',
      message: syncConfirmMessage(displayRows),
      displayCancelBtn: 'yes',
      cancelActionName: 'Cancel',
      actionName: 'Update invoice',
      onActionBtnClick: (selectionType) => {
        if (selectionType !== 'close') {
          runSyncFromOrder();
        }
      },
    });
  };

  const helpLink = (
    <Link
      component="button"
      type="button"
      variant="body2"
      data-testid="discrepancy-help-link"
      onClick={() => setHelpOpen(true)}
    >
      How this works
    </Link>
  );

  const decisionSummary = latestDecision ? (
    <Typography variant="body2" color="textSecondary" className={compact ? 'mb-8' : 'mb-16'}>
      {DECISION_LABELS[latestDecision.decision] || latestDecision.decision}
      {latestDecision.decided_by_email ? ` by ${latestDecision.decided_by_email}` : ''}
      {latestDecision.decided_at
        ? ` on ${formatDateString(latestDecision.decided_at, 'MMM dd, yyyy HH:mm')}`
        : ''}
      {!hasDiscrepancy
        ? ' — figures now agree'
        : latestDecision.matches_current_snapshot === false
          ? ' — figures have changed since that decision'
          : ''}
      {hasDiscrepancy && latestDecision.decision === 'FLAGGED_FOR_CORRECTION'
        ? '. Raise a credit or debit note to restate what is owed.'
        : ''}
    </Typography>
  ) : null;

  if (!warningActive) {
    if (!decisionSummary) {
      return null;
    }
    return (
      <div className="print:hidden">
        {decisionSummary}
        {!readOnly ? (
          <Stack direction="row" spacing={1} alignItems="center" className="mt-8">
            {/* Only a server that can undo sends has_adjustment_notes, so the order the two ship in doesn't matter. */}
            {hasDiscrepancy && latestDecision?.decision === 'ACCEPTED' && 'has_adjustment_notes' in discrepancy ? (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                disabled={submitting}
                data-testid="discrepancy-undo"
                onClick={() => handleDecision('REOPENED')}
              >
                Undo
              </Button>
            ) : null}
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => setRaiseOpen(true)}
            >
              Raise credit/debit note
            </Button>
            {helpLink}
          </Stack>
        ) : null}
        <InvoiceDiscrepancyHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
        {!readOnly && raiseOpen ? (
          <RaiseAdjustmentNoteDialog
            open={raiseOpen}
            onClose={() => setRaiseOpen(false)}
            invoiceId={invoiceId}
            defaultAmount={noteRaised ? undefined : defaultNoteAmount}
            defaultNoteType={defaultNoteType}
            discrepancyDecisionId={latestDecision?.id}
            onCreated={() => {
              setNoteRaised(true);
              if (onUpdated) onUpdated(discrepancy);
              if (onNoteRaised) onNoteRaised();
            }}
          />
        ) : null}
      </div>
    );
  }

  const comparison = compact ? (
    <Stack spacing={1} className="mb-12">
      {displayRows.map((row) => (
        <Box
          key={`${row.order_id}-${row.component}`}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" className="mb-4">
            Order {row.order_no || row.order_id}
            {' · '}
            {COMPONENT_LABELS[row.component] || row.component}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              Invoice: <strong>{row.invoice_amount != null ? formatter.format(row.invoice_amount) : '—'}</strong>
            </Typography>
            <Typography variant="body2">
              Order: <strong>{formatAmount(row)}</strong>
            </Typography>
          </Stack>
          <Typography variant="body2" className="mt-4" data-testid="discrepancy-difference">
            {differenceSentence(row.delta)}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" className="mt-4">
            Invoice as of {row.invoice_as_of ? formatDateString(row.invoice_as_of) : '—'}
            {' · '}
            Order as of {row.order_as_of ? formatDateString(row.order_as_of) : '—'}
          </Typography>
        </Box>
      ))}
    </Stack>
  ) : (
    <TableContainer
      className="mb-16"
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow>
            <TableCell>Order</TableCell>
            <TableCell>Component</TableCell>
            <TableCell align="right">Invoice figure</TableCell>
            <TableCell align="right">Current order figure</TableCell>
            <TableCell align="right">Difference</TableCell>
            <TableCell>As of</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows.map((row) => (
            <TableRow key={`${row.order_id}-${row.component}`}>
              <TableCell>{row.order_no || row.order_id}</TableCell>
              <TableCell>{COMPONENT_LABELS[row.component] || row.component}</TableCell>
              <TableCell align="right">
                {row.invoice_amount != null ? formatter.format(row.invoice_amount) : '—'}
              </TableCell>
              <TableCell align="right">{formatAmount(row)}</TableCell>
              <TableCell align="right">
                {row.delta != null ? differenceLabel(row.delta) : '—'}
              </TableCell>
              <TableCell>
                <Typography variant="caption" display="block">
                  Invoice: {row.invoice_as_of ? formatDateString(row.invoice_as_of) : '—'}
                </Typography>
                <Typography variant="caption" display="block">
                  Order: {row.order_as_of ? formatDateString(row.order_as_of) : '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const noteField = readOnly ? null : (
    <TextField
      label="Note (optional)"
      value={note}
      onChange={(e) => setNote(e.target.value)}
      size="small"
      fullWidth
      className="mb-12"
      multiline
      minRows={compact ? 1 : 2}
      inputProps={{ maxLength: 1000 }}
    />
  );

  const raiseButton = (label, variant) => (
    <Button
      variant={variant}
      color="secondary"
      size={compact ? 'small' : 'medium'}
      disabled={submitting}
      data-testid="discrepancy-raise"
      onClick={() => setRaiseOpen(true)}
    >
      {label}
    </Button>
  );

  const recommendedNote = action === 'credit' || action === 'debit';

  // PGSS-5862: one main action, by what can still be done. The others stay, smaller.
  const actionButtons = readOnly ? null : (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {action === 'update' ? (
          <Button
            variant="contained"
            color="primary"
            size={compact ? 'small' : 'medium'}
            disabled={submitting}
            data-testid="discrepancy-update"
            onClick={handleSyncFromOrder}
          >
            Update invoice to match order
          </Button>
        ) : null}
        {recommendedNote
          ? raiseButton(`Raise ${action} note for ${formatter.format(defaultNoteAmount)}`, 'contained')
          : null}
        <Button
          variant={action === 'keep' ? 'contained' : 'outlined'}
          color={action === 'keep' ? 'primary' : 'inherit'}
          size={compact ? 'small' : 'medium'}
          disabled={submitting}
          data-testid="discrepancy-keep"
          onClick={() => handleDecision('ACCEPTED')}
        >
          Keep invoice as it is
        </Button>
        <Button
          variant="outlined"
          color="warning"
          size={compact ? 'small' : 'medium'}
          disabled={submitting}
          data-testid="discrepancy-flag"
          onClick={() => handleDecision('FLAGGED_FOR_CORRECTION')}
        >
          Flag for correction
        </Button>
        {!recommendedNote ? raiseButton('Raise credit/debit note', 'outlined') : null}
      </Stack>
      <Typography variant="caption" color="textSecondary" display="block" className="mt-8">
        Keep invoice as it is leaves the difference in place and clears this warning. Flag for correction marks it to
        fix later. Neither changes the invoice or what the customer owes.
      </Typography>
    </>
  );

  // A note raised here isn't in this payload yet.
  const guidanceText = discrepancyGuidance(
    { ...discrepancy, has_adjustment_notes: discrepancy.has_adjustment_notes || noteRaised },
    action,
    readOnly
  );

  return (
    <Alert
      severity="warning"
      className="print:hidden mb-24"
      sx={{
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        '& .MuiAlert-message': {
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          minWidth: 0,
        },
      }}
      action={
        compact ? (
          <IconButton
            aria-label={detailsOpen ? 'Hide discrepancy details' : 'Show discrepancy details'}
            color="inherit"
            size="small"
            onClick={() => setDetailsOpen((open) => !open)}
          >
            {detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        ) : null
      }
    >
      <AlertTitle>
        This invoice doesn&apos;t match its order
      </AlertTitle>
      <Typography variant="body2" className={compact ? 'mb-8' : 'mb-12'}>
        {guidanceText}
        {' '}
        {helpLink}
      </Typography>

      {decisionSummary}

      {compact && (
        <Typography
          variant="body2"
          color="textSecondary"
          className="mb-8"
          sx={{ cursor: 'pointer' }}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {compactSummaryLine(displayRows)}
          {' · '}
          <Box component="span" sx={{ textDecoration: 'underline' }}>
            {detailsOpen ? 'Hide details' : 'Show details'}
          </Box>
        </Typography>
      )}

      {compact ? (
        <>
          <Collapse in={detailsOpen}>
            {comparison}
            {noteField}
          </Collapse>
          {actionButtons}
        </>
      ) : (
        <>
          {comparison}
          {noteField}
          {actionButtons}
        </>
      )}
      <InvoiceDiscrepancyHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      {raiseOpen && !readOnly ? (
        <RaiseAdjustmentNoteDialog
          open={raiseOpen}
          onClose={() => setRaiseOpen(false)}
          invoiceId={invoiceId}
          defaultAmount={noteRaised ? undefined : defaultNoteAmount}
          defaultNoteType={defaultNoteType}
          discrepancyDecisionId={latestDecision?.id}
          onCreated={() => {
            setNoteRaised(true);
            if (onUpdated) onUpdated(discrepancy);
            if (onNoteRaised) onNoteRaised();
          }}
        />
      ) : null}
    </Alert>
  );
}

export default InvoiceDiscrepancyPanel;
